import { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Combobox, ETypeOfNotification, setNotification } from 'medere-ui-framework';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Establishment } from '../../interface/interface';
import { UserSessionDTO, HandoffReceiptDTO, AuthorizedDeskDTO } from '../../dto/locationcash/LocationCashDTO';

import { assignUser, getPendingHandoffReceipt, getAuthorizedDesks } from '../../services/LocationCashController';
import { useLocationDeskContext } from './context/LocationDeskContext';
import useResizeListener from '../../hooks/useResizeListener';
import { IncomingHandoffModal } from './components/HandoffModal';
import TakeOverModal from './components/TakeOverModal';
import { loadAllEstablishments } from '../../servicesHooks/Location/useLocationConfigService';
import { parseApiError } from '../../services/apiError';
import { getPreferredBuilding } from '../../servicesHooks/ProfileDataForm/useProfileDataFormService';

export default function DeskSelectionView() {
    const isMobile = useResizeListener(768);
    const dispatch = useDispatch();
    const { setCurrentView, setActiveSession, setActiveLocationId, currentUserId, navigateToMovements } = useLocationDeskContext();

    const [establishments, setEstablishments] = useState<Establishment[]>([]);
    const [selectedEstablishment, setSelectedEstablishment] = useState<Establishment | null>(null);
    const [desks, setDesks] = useState<AuthorizedDeskDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingDesks, setLoadingDesks] = useState(false);
    const [assigningId, setAssigningId] = useState<number | null>(null);
    const [takeOverDesk, setTakeOverDesk] = useState<AuthorizedDeskDTO | null>(null);

    // Estado para receipt pendiente al asignarse
    const [pendingReceipt, setPendingReceipt] = useState<HandoffReceiptDTO | null>(null);
    const [pendingLocationId, setPendingLocationId] = useState<number | null>(null);

    useEffect(() => {
        Promise.all([
            loadAllEstablishments(),
            // Establecimiento de Mi perfil o, si no hay, el del puesto abierto. Solo precarga: el combo se puede cambiar.
            getPreferredBuilding().catch(() => null),
        ])
            .then(([data, preferredBuilding]) => {
                const active = (data ?? []).filter(e => e.active);
                setEstablishments(active);

                if (preferredBuilding?.id != null) {
                    const match = active.find(e => e.buildingid === preferredBuilding.id);
                    if (match) {
                        setSelectedEstablishment(match);
                        return;
                    }
                }

                if (active.length === 1) {
                    setSelectedEstablishment(active[0]);
                }
            })
            .catch((err) => {
                console.error(err);
                dispatch(setNotification({
                    title: 'Error',
                    desc: 'No se pudieron cargar los establecimientos',
                    icon: 'pi-times',
                    typeOfNotification: ETypeOfNotification.ERROR,
                }));
            })
            .finally(() => setLoading(false));
    }, []);

    const loadDesksForBuilding = useCallback(async (buildingId: number) => {
        setLoadingDesks(true);
        try {
            const deskList = await getAuthorizedDesks(buildingId);
            setDesks(deskList);
        } catch (err) {
            console.error(err);
            dispatch(setNotification({
                title: 'Error',
                desc: 'No se pudieron cargar los puestos de atención',
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
        } finally {
            setLoadingDesks(false);
        }
    }, [dispatch]);

    useEffect(() => {
        if (selectedEstablishment) {
            loadDesksForBuilding(selectedEstablishment.buildingid);
        } else {
            setDesks([]);
        }
    }, [selectedEstablishment, loadDesksForBuilding]);

    const navigateToActive = (session: UserSessionDTO, locationId: number) => {
        setActiveSession(session);
        setActiveLocationId(locationId);
        setCurrentView('active');
    };

    const handleAssign = async (desk: AuthorizedDeskDTO) => {
        setAssigningId(desk.hospitalLocationId);
        try {
            // Chequear si hay un receipt pendiente ANTES de asignar.
            // Si la consulta falla, no se asigna: el error cae al catch externo.
            const receipt = await getPendingHandoffReceipt(desk.hospitalLocationId);
            if (receipt) {
                // Hay receipt pendiente → mostrar modal, NO asignar todavía
                setPendingReceipt(receipt);
                setPendingLocationId(desk.hospitalLocationId);
            } else {
                const session = await assignUser(desk.hospitalLocationId);
                navigateToActive(session, desk.hospitalLocationId);
            }
        } catch (err) {
            const apiError = parseApiError(err);
            if (apiError.status === 409) {
                dispatch(setNotification({
                    title: 'No se pudo asignar',
                    desc: apiError.message,
                    icon: 'pi-exclamation-triangle',
                    typeOfNotification: ETypeOfNotification.WARNING,
                }));
                if (selectedEstablishment) {
                    loadDesksForBuilding(selectedEstablishment.buildingid);
                }
            } else {
                dispatch(setNotification({
                    title: 'No se pudo asignar el puesto de atención',
                    desc: apiError.message,
                    icon: 'pi-times',
                    typeOfNotification: ETypeOfNotification.ERROR,
                }));
            }
        } finally {
            setAssigningId(null);
        }
    };

    const handleGoToActive = (hospitalLocationId: number) => {
        setActiveSession(null);
        setActiveLocationId(hospitalLocationId);
        setCurrentView('active');
    };

    const handleTakeOverSuccess = (session: UserSessionDTO) => {
        const locationId = takeOverDesk?.hospitalLocationId ?? session.hospitalLocationId;
        setTakeOverDesk(null);
        navigateToActive(session, locationId);
    };

    const handleIncomingHandoffSuccess = (session: UserSessionDTO) => {
        navigateToActive(session, session.hospitalLocationId);
        setPendingReceipt(null);
        setPendingLocationId(null);
    };

    // ── Templates para columnas del DataTable ──

    const deskNameTemplate = (desk: AuthorizedDeskDTO) => (
        <div>
            <span className="font-semibold">{desk.description}</span>
            <br />
            <span className="desk-table-sector">{desk.sectorDescription}</span>
            {desk.hasCashRegister && (
                <span className="desk-card-badge desk-card-badge--cash ml-2">
                    <i className="pi pi-dollar" />
                    Caja
                </span>
            )}
        </div>
    );

    const occupiedByTemplate = (desk: AuthorizedDeskDTO) => {
        if (!desk.activeSession) {
            return <span className="desk-card-badge desk-card-badge--free">Libre</span>;
        }
        const isMe = desk.activeSession.userId === currentUserId;
        return (
            <span className={`desk-card-badge ${isMe ? 'desk-card-badge--mine' : 'desk-card-badge--occupied'}`}>
                {desk.activeSession.username}
            </span>
        );
    };

    const actionsTemplate = (desk: AuthorizedDeskDTO) => {
        const isFree = !desk.activeSession;
        const isMe = !isFree && desk.activeSession!.userId === currentUserId;
        const isOther = !isFree && !isMe;
        const isAssigning = assigningId === desk.hospitalLocationId;

        return (
            <div className="flex gap-2">
                {isFree && (
                    <Button
                        icon="pi pi-sign-in"
                        className="buttonMain p-button-sm"
                        tooltip="Asignarme"
                        tooltipOptions={{ position: 'top' }}
                        loading={isAssigning}
                        onClick={() => handleAssign(desk)}
                    />
                )}
                {isMe && (
                    <Button
                        icon="pi pi-arrow-right"
                        className="buttonMain3 p-button-sm"
                        tooltip="Ir al puesto"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => handleGoToActive(desk.hospitalLocationId)}
                    />
                )}
                {isOther && desk.allowsTakeover && (
                    <Button
                        icon="pi pi-user-edit"
                        className="buttonMain2 p-button-sm"
                        tooltip="Tomar puesto"
                        tooltipOptions={{ position: 'top' }}
                        loading={isAssigning}
                        onClick={() => setTakeOverDesk(desk)}
                    />
                )}
                {desk.hasCashRegister && (
                    <Button
                        icon="pi pi-eye"
                        className="buttonMain3 p-button-sm"
                        tooltip="Ver movimientos"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => navigateToMovements(desk.hospitalLocationId)}
                    />
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div>
            <h1 className="desk-view-title">Selección de puesto de atención</h1>
            <p className="desk-view-subtitle">Seleccionar el puesto para iniciar el turno.</p>

            <div className="mb-4" style={{ maxWidth: isMobile ? '100%' : '350px' }}>
                <Combobox
                    label="Establecimiento"
                    list={establishments}
                    value={selectedEstablishment}
                    setValue={(e: Establishment | null) => setSelectedEstablishment(e)}
                    optionLabel="buildingname"
                    placeholder="Seleccionar establecimiento..."
                />
            </div>

            {loadingDesks ? (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <ProgressSpinner />
                </div>
            ) : desks.length === 0 && selectedEstablishment ? (
                <div className="desk-empty-state">
                    <i className="pi pi-inbox" />
                    <p>No hay puestos de atención activos en este establecimiento.</p>
                </div>
            ) : desks.length > 0 ? (
                <DataTable
                    value={desks}
                    size="small"
                    stripedRows
                    emptyMessage="No hay puestos de atención."
                    dataKey="hospitalLocationId"
                >
                    <Column header="Puesto" body={deskNameTemplate} style={{ minWidth: '200px' }} />
                    <Column header="Ocupado por" body={occupiedByTemplate} style={{ width: '180px' }} />
                    <Column header="Acciones" body={actionsTemplate} style={{ width: '150px' }} />
                </DataTable>
            ) : null}

            {pendingReceipt && (
                <IncomingHandoffModal
                    visible={!!pendingReceipt}
                    onHide={() => {
                        setPendingReceipt(null);
                        setPendingLocationId(null);
                    }}
                    receipt={pendingReceipt}
                    onSuccess={handleIncomingHandoffSuccess}
                />
            )}

            {takeOverDesk && (
                <TakeOverModal
                    visible
                    hospitalLocationId={takeOverDesk.hospitalLocationId}
                    locationDescription={takeOverDesk.description}
                    previousUsername={takeOverDesk.activeSession?.username ?? 'otro usuario'}
                    hasCashRegister={takeOverDesk.hasCashRegister}
                    onHide={() => setTakeOverDesk(null)}
                    onSuccess={handleTakeOverSuccess}
                />
            )}
        </div>
    );
}
